import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { UserCalendarType } from "../../types/userCalendars/type";
import userCalendarsAPI from "../../api/userCalendarsAPI";
import { AccessRole, UserCalendarAccessType } from "../../types/userCalendars/enum";

export const userCalendarsQueryKeys = {
  all: () => ["user-calendar"] as const,
  detail: (id: number) => [...userCalendarsQueryKeys.all(), id] as const,
} as const;

interface FetchUserCalendarsQueryProps {
  options?: Omit<UseQueryOptions<UserCalendarType[]>, "queryKey" | "queryFn" | "select">;
  calendarAccessType?: UserCalendarAccessType;
}
export function useFetchUserCalendarsQuery({
  options = {},
  calendarAccessType,
}: FetchUserCalendarsQueryProps = {}) {
  return useQuery<UserCalendarType[]>({
    queryKey: userCalendarsQueryKeys.all(),
    queryFn: userCalendarsAPI.getCalendars,
    select: (data) => {
      // 기본적으로 displayOrder 기준으로 정렬
      const sortedData = data.sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

      // editable 또는 readonly에 따라 필터링
      if (calendarAccessType === UserCalendarAccessType.EDITABLE) {
        return sortedData.filter(
          (calendar) =>
            calendar.accessRole === AccessRole.OWNER || calendar.accessRole === AccessRole.WRITER
        );
      }
      if (calendarAccessType === UserCalendarAccessType.READONLY) {
        return sortedData.filter(
          (calendar) =>
            calendar.accessRole === AccessRole.READER ||
            calendar.accessRole === AccessRole.FREE_BUSY_READER
        );
      }
      // 플래그가 없으면 전체 데이터를 반환
      return sortedData;
    },
    ...options,
  });
}

export function useFetchUserCalendarDetailQuery(
  id: number,
  options?: Omit<UseQueryOptions<UserCalendarType>, "queryKey" | "queryFn">
) {
  return useQuery<UserCalendarType>({
    queryKey: userCalendarsQueryKeys.detail(id),
    queryFn: () => userCalendarsAPI.getCalendarDetail(id),
    ...options,
  });
}
